/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';

import { saveAs } from '@ibm-watson/shared/modules/actions/util/download';
import { endpoints } from '@ibm-watson/shared/modules/ia/util/config';

const buildExistingValueUrl = (workspaceId, entityId, valueId, skillId) => {
  return skillId
    ? `${endpoints.store}/v2/skills/${skillId}/workspace/entities/${entityId}/values/${valueId}/recommendations`
    : `${endpoints.store}/v1/workspaces/${workspaceId}/entities/${entityId}/values/${valueId}/recommendations`;
};

const buildNewValueUrl = (workspaceId, entityId, skillId) => {
  return skillId
    ? `${endpoints.store}/v2/skills/${skillId}/workspace/entities/${entityId}/recommendations`
    : `${endpoints.store}/v1/workspaces/${workspaceId}/entities/${entityId}/recommendations`;
};
const buildRecommendationSourcesURL = (workspaceId, skillId) => {
  return skillId
    ? `${endpoints.store}/v2/skills/${skillId}/workspace/recommendation_sources`
    : `${endpoints.store}/v1/workspaces/${workspaceId}/recommendation_sources`;
};

const exportRecommendsEndpoint = (workspaceId, skillId) => {
  return skillId
    ? `${endpoints.store}/v2/skills/${skillId}/workspace/recommended_intents?beta=true&export=true`
    : `${endpoints.store}/v1/workspaces/${workspaceId}/recommendation_intents?beta=true&export=true`;
};

export function listSynonymRecommendationsForExistingValue(
  workspaceId,
  entityId,
  valueId,
  ignoredSuggestions,
  includeDetails,
  skillId,
  invokeApi = false
) {
  const url = buildExistingValueUrl(workspaceId, entityId, valueId, skillId);
  const params = {};
  if (ignoredSuggestions && ignoredSuggestions.length > 0) {
    params.negative_examples = ignoredSuggestions.join(',');
  }

  if (includeDetails) {
    params.details = includeDetails;
  }

  const args = { params };

  if (skillId) {
    args.headers = { 'X-Watson-Workspace-Id': workspaceId };
  }
  if (!invokeApi) {
    return Promise.resolve({ recommendations: [] });
  }
  return axios
    .get(url, args)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

export function listSynonymRecommendationsForNewValue(
  workspaceId,
  entityId,
  valueId,
  synonyms,
  ignoredSuggestions,
  includeDetails,
  skillId,
  invokeApi = false
) {
  const url = buildNewValueUrl(workspaceId, entityId, skillId);
  const params = { value: valueId };

  if (synonyms && synonyms.length > 0) {
    params.synonyms = synonyms.join(',');
  }

  if (ignoredSuggestions && ignoredSuggestions.length > 0) {
    params.negative_examples = ignoredSuggestions.join(',');
  }

  if (includeDetails) {
    params.details = includeDetails;
  }

  const args = { params };

  if (skillId) {
    args.headers = { 'X-Watson-Workspace-Id': workspaceId };
  }
  if (!invokeApi) {
    return Promise.resolve({ recommendations: [] });
  }
  return axios
    .get(url, args)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

export function listRecommendationSources(
  workspaceId,
  skillId,
  showingNewIA = false,
  initialCall = false
) {
  const config = {};
  let url = buildRecommendationSourcesURL(workspaceId, skillId);
  url += initialCall ? '?details=false' : '';

  if (initialCall && showingNewIA) {
    url += '&new_ia=' + showingNewIA;
  } else if (showingNewIA) {
    url += '?new_ia=' + showingNewIA;
  }

  if (skillId) {
    config.headers = { 'X-Watson-Workspace-Id': workspaceId };
  }

  return axios
    .get(url, config)
    .then(response => response)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

export function setRecommendationSources(
  workspaceId,
  skillId,
  sources,
  showingNewIA = false
) {
  const data = { recommendation_sources: sources };
  const config = {};
  let url =
    buildRecommendationSourcesURL(workspaceId, skillId) + '?details=true';

  if (showingNewIA) {
    url += '&new_ia=' + showingNewIA;
  }

  if (skillId) {
    config.headers = { 'X-Watson-Workspace-Id': workspaceId };
  }
  return axios
    .put(url, data, config)
    .then(response => response)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

export const exportRecommends = (workspaceId, skillId) => {
  const url = exportRecommendsEndpoint(workspaceId, skillId);
  return axios
    .get(url, {
      headers: {
        Accept: 'text/csv'
      }
    })
    .then(response => {
      const data = new Blob([response.data], {
        type: 'text/csv'
      });
      const url = window.URL.createObjectURL(data);
      saveAs(url, `${skillId}_recommendations.csv`);
    })
    .catch(error => Promise.reject(error));
};
